// Logique pure de l'impersonation (messika-pwa#10) : aucune dépendance React / AppWrite.

export const IMPERSONATE_HEADER = 'X-Appwrite-Impersonate-User-Email'

// En-tête envoyé au webhook (avec le JWT de l'opérateur) pendant une impersonation (messika-pwa#13).
export const IMPERSONATE_WEBHOOK_HEADER = 'X-Impersonate-User-Email'

export type LogoutStep = 'removeHeader' | 'deleteSessions' | 'forgetOperator' | 'purgeDb' | 'forgetImpersonation'

/** Pose (email) ou retire (null / vide) l'en-tête d'impersonation sur un objet `headers`. */
export function applyImpersonationHeader(headers: Record<string, string>, targetEmail: string | null | undefined): void {
   const email = (targetEmail ?? '').trim()
   if (email) headers[IMPERSONATE_HEADER] = email
   else delete headers[IMPERSONATE_HEADER]
}

const norm = (v: any): string => (typeof v === 'string' ? v.trim().toLowerCase() : '')

/**
 * Vrai ssi account.get() (en-tête posé) renvoie bien la CIBLE : email égal à la cible et $id différent de celui
 * de la session (l'opérateur). Le SDK envoie Response-Format 1.0 : impersonatorUserId n'est pas fourni, on ne peut
 * pas l'exiger. S'il est présent (format récent) il suffit, sauf si l'email renvoyé diffère de la cible.
 */
export function isImpersonationVerified(accountGetResult: any, targetEmail: string, sessionUserId: string): boolean {
   if (!accountGetResult) return false
   const email = norm(accountGetResult.email)
   const target = norm(targetEmail)
   if (email && email !== target) return false
   const impId = accountGetResult.impersonatorUserId
   if (typeof impId === 'string' && impId.trim() !== '') return true
   const id = accountGetResult.$id
   return email !== '' && email === target && typeof id === 'string' && id !== '' && id !== sessionUserId
}

export type ImpersonationRefusal =
   | { cause: 'noRight' }
   | { cause: 'unexpected', email: string }
   | { cause: 'error', message: string }

/** Cause d'un refus : erreur d'account.get(), opérateur renvoyé tel quel (droit absent), ou autre email. */
export function impersonationRefusal(accountGetResult: any, sessionUserId: string, error?: any): ImpersonationRefusal {
   if (error) return { cause: 'error', message: String(error?.message ?? error) }
   const id = accountGetResult?.$id
   if (typeof id === 'string' && id !== '' && id === sessionUserId) return { cause: 'noRight' }
   return { cause: 'unexpected', email: String(accountGetResult?.email ?? '') }
}

/**
 * Refus d'impersonation renvoyé par le webhook : 403 `impersonation_forbidden` (droit impersonator absent / retiré)
 * ou 404 `impersonation_target_not_found`. Un 403 / 404 ordinaire (sans ce code) donne null.
 */
export function impersonationWebhookError(err: any): 'forbidden' | 'target_not_found' | null {
   const status = err?.response?.status
   const code = err?.response?.data?.code
   if (status === 403 && code === 'impersonation_forbidden') return 'forbidden'
   if (status === 404 && code === 'impersonation_target_not_found') return 'target_not_found'
   return null
}

export type LogoutMode = 'logout' | 'exit'

/**
 * Ordre des étapes de déconnexion. En impersonation, l'en-tête est retiré AVANT la suppression de session.
 * `logout` (messika-pwa#16) : cible et données locales conservées (opérateur oublié, pas de purge).
 * `exit` : sortie d'impersonation, purge totale et oubli de la cible.
 */
export function logoutSequence(isImpersonating: boolean, mode: LogoutMode = 'logout'): LogoutStep[] {
   if (!isImpersonating) return ['deleteSessions']
   return mode === 'exit'
      ? ['removeHeader', 'deleteSessions', 'purgeDb', 'forgetImpersonation']
      : ['removeHeader', 'deleteSessions', 'forgetOperator']
}
